import Bowser from 'bowser';

type SupportedBrowserName = 'firefox' | 'safari' | 'chrome' | 'microsoft edge';

const mapPermissionNameToMediaDeviceName = (permissionStatus: PermissionName | 'camera' | 'microphone'): string => {
  if (permissionStatus === 'microphone') {
    return 'audio';
  }
  if (permissionStatus === 'camera') {
    return 'video';
  }
  return permissionStatus;
};

export const userAgentInfo = (() => {
  if (typeof window === 'undefined') {
    return null;
  }
  const { userAgent } = window.navigator;
  if (!userAgent) {
    return null;
  }
  return Bowser.parse(userAgent);
})();

export const isBrowser = (browser: SupportedBrowserName): boolean => {
  if (!userAgentInfo) {
    return false;
  }

  return userAgentInfo.browser.name?.toLowerCase() === browser;
};

type NavigatorWithUserAgentData = {
  userAgentData?: {
    mobile: boolean;
  };
} & Navigator;

// platform.type is 'desktop' if on desktop
export const isDesktop = (() => {
  if (typeof window === 'undefined') {
    return false;
  }

  // If Bowser detected platform type, use it
  if (userAgentInfo?.platform.type) {
    return userAgentInfo.platform.type === 'desktop';
  }

  // Fallback for cases where platform.type is not populated (e.g. Chrome OS)
  // Use User-Agent Client Hints API if available (Chromium-based browsers)
  const userAgentData = (navigator as NavigatorWithUserAgentData)?.userAgentData;
  if (userAgentData) {
    return !userAgentData.mobile;
  }

  return false;
})();

// Some browser doesn't support Permissions API (e.g. safari version 15.6 and before)
export const isPermissionsApiAvailable = (() => {
  if (!userAgentInfo) {
    return false;
  }
  if (isBrowser('safari')) {
    const versionNumber = parseInt(userAgentInfo.browser.version || '', 10);
    if (!versionNumber || versionNumber < 16) {
      return false;
    }
  }
  return true;
})();

export async function getHardwarePermissionStatus(name: PermissionName): Promise<Pick<PermissionStatus, 'state'>> {
  if (!isPermissionsApiAvailable) {
    try {
      await navigator.mediaDevices.getUserMedia({
        [mapPermissionNameToMediaDeviceName(name)]: true,
      });
      return {
        state: 'granted',
      };
    } catch {
      return {
        state: 'denied',
      };
    }
  }
  switch (userAgentInfo?.browser.name?.toLowerCase() as SupportedBrowserName) {
    case 'firefox': {
      try {
        const result = await navigator.mediaDevices.getUserMedia({
          video: name === ('camera' as PermissionName),
          audio: name === ('microphone' as PermissionName),
        });
        return {
          state: result.active ? 'granted' : 'denied',
        };
      } catch {
        return {
          state: 'denied',
        };
      }
    }
    default: {
      return navigator.permissions.query({ name });
    }
  }
}

export async function getDeviceInfo() {
  const devices = (await navigator.mediaDevices?.enumerateDevices()) ?? [];

  return {
    audioInputDevices: devices.filter(device => device.kind === 'audioinput'),
    videoInputDevices: devices.filter(device => device.kind === 'videoinput'),
    audioOutputDevices: devices.filter(device => device.kind === 'audiooutput'),
    hasAudioInputDevices: devices.some(device => device.kind === 'audioinput'),
    hasVideoInputDevices: devices.some(device => device.kind === 'videoinput'),
  };
}

export async function getHardwarePermissionStates(): Promise<{
  cameraPermissionState: PermissionState;
  microphonePermissionState: PermissionState;
}> {
  const cameraPermissionState = (await getHardwarePermissionStatus('camera' as PermissionName)).state;
  const microphonePermissionState = (await getHardwarePermissionStatus('microphone' as PermissionName)).state;

  return {
    cameraPermissionState,
    microphonePermissionState,
  };
}
